import * as React from 'react'

// Window-level barcode-scanner capture. USB barcode scanners emit a burst of
// keystrokes ending with Enter — whichever element has focus receives them.
// We listen at the window so a scan still registers if focus drifted off the
// scan input (e.g. user clicked Report damage or any row action).
//
// Behaviour:
// - When the active element is an <input>, <textarea>, or contenteditable,
//   we do nothing — the native handler owns those keystrokes.
// - Otherwise we accumulate printable chars into a buffer. Enter commits
//   the buffer if it's long enough to plausibly be a barcode.
// - 500ms idle clears the buffer so stray keystrokes don't bleed across
//   scans.
//
// MIN_LEN keeps a stray Enter from firing an empty scan.
const MIN_LEN = 4
const IDLE_RESET_MS = 500

export function useBarcodeScanner(onScan: (barcode: string) => void) {
  const onScanRef = React.useRef(onScan)
  React.useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  React.useEffect(() => {
    const buffer = { value: '', lastTime: 0 }

    function isTextInput(el: Element | null): boolean {
      if (!el) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return true
      return (el as HTMLElement).isContentEditable === true
    }

    function handleKey(e: KeyboardEvent) {
      // If the user is typing into an input/textarea, native handlers own it.
      // The scan input itself runs its own Enter handler.
      if (isTextInput(document.activeElement)) return

      const now = Date.now()
      if (now - buffer.lastTime > IDLE_RESET_MS) buffer.value = ''
      buffer.lastTime = now

      if (e.key === 'Enter') {
        if (buffer.value.length >= MIN_LEN) {
          const code = buffer.value
          buffer.value = ''
          e.preventDefault()
          onScanRef.current(code)
        }
        return
      }

      if (e.key.length === 1) buffer.value += e.key
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])
}
